// ui/src/components/shared/PlanVersionSwitcher.jsx

/**
 * @file PlanVersionSwitcher.jsx
 * @description Controlled tab-style version switcher for toggling between an
 *   approved plan version (read-only) and a draft amendment version (editable).
 *   Keyboard navigable (arrow keys, Home, End). No API calls — prop-driven.
 *   Used by PlanWorkspace to switch between V_current and V_new during amendment.
 * @module shared
 * @author data-fit LLC · DecisionFlow · S775
 * @see api_spec_fileA_v4_final.md §0.11 — V_current / V_new amendment routing
 *
 * @changelog
 *   S815  Dual prop alias: accepts both activeVersionId/version.id (canonical)
 *         and activeVersion/version.value (AmendmentStateBanner shape) so the
 *         component works with both call sites without breaking either.
 */

import { useRef } from 'react';

/**
 * @typedef {{
 *   id?: string,
 *   value?: string,
 *   label: string,
 *   version?: number,
 *   readOnly?: boolean,
 *   status?: string
 * }} VersionOption
 */

/**
 * Controlled version switcher.
 *
 * Accepts two naming conventions:
 *   Canonical:  activeVersionId + version.id
 *   Banner:     activeVersion   + version.value
 *
 * @param {{
 *   versions: VersionOption[],
 *   activeVersionId?: string,
 *   activeVersion?: string,
 *   onSwitch: (versionId: string) => void,
 *   className?: string
 * }} props
 */
export default function PlanVersionSwitcher({
  versions,
  activeVersionId,
  activeVersion,
  onSwitch,
  className = '',
}) {
  // WHY: Support both naming conventions. AmendmentStateBanner passes
  // activeVersion + version.value; other consumers pass activeVersionId + version.id.
  const resolvedActiveId = activeVersionId ?? activeVersion ?? null;

  const tabRefs = useRef([]);

  function getVersionKey(version) {
    return version.id ?? version.value ?? '';
  }

  function focusAndSwitch(index) {
    if (!versions.length) return;

    const normalizedIndex =
      ((index % versions.length) + versions.length) % versions.length;

    const version = versions[normalizedIndex];
    onSwitch(getVersionKey(version));
    tabRefs.current[normalizedIndex]?.focus();
  }

  function handleKeyDown(event, index) {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        focusAndSwitch(index + 1);
        break;

      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        focusAndSwitch(index - 1);
        break;

      case 'Home':
        event.preventDefault();
        focusAndSwitch(0);
        break;

      case 'End':
        event.preventDefault();
        focusAndSwitch(versions.length - 1);
        break;

      default:
        break;
    }
  }

  return (
    <div
      role="tablist"
      aria-label="Plan versions"
      className={[
        'inline-flex rounded-[999px]',
        'border border-[rgb(var(--color-border))]',
        'bg-[rgb(var(--color-surface-muted))]',
        'p-1',
        className,
      ].join(' ')}
    >
      {versions.map((version, index) => {
        const versionKey = getVersionKey(version);
        const isActive = versionKey === resolvedActiveId;
        const isApproved = version.status === 'approved';

        const stateLabel = isApproved
          ? 'Read only'
          : version.readOnly
            ? 'Read only'
            : 'Editable draft';

        return (
          <button
            key={versionKey || index}
            ref={(node) => {
              tabRefs.current[index] = node;
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-label={`${version.label}. ${stateLabel}.`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onSwitch(versionKey)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={[
              'flex min-w-[128px] items-center gap-2 rounded-[999px]',
              'px-3 py-1.5 text-left',
              'transition-colors',
              'focus-visible:outline-none',
              'focus-visible:ring-2',
              'focus-visible:ring-[rgb(var(--color-focus-ring))]',
              isActive
                ? 'bg-[rgb(var(--color-primary))] text-[rgb(var(--color-surface))]'
                : [
                    'bg-[rgb(var(--color-surface-muted))]',
                    'text-[rgb(var(--color-text))]',
                    'hover:bg-[rgb(var(--color-selected-bg))]',
                  ].join(' '),
            ].join(' ')}
          >
            <span
              aria-hidden="true"
              className="flex h-5 w-5 shrink-0 items-center justify-center"
            >
              {isApproved || version.readOnly ? (
                <svg
                  viewBox="0 0 20 20"
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                >
                  <rect x="4.5" y="8.5" width="11" height="8" rx="2" />
                  <path d="M7 8.5V6.7a3 3 0 0 1 6 0v1.8" />
                </svg>
              ) : (
                <svg
                  viewBox="0 0 20 20"
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                >
                  <path d="M4 14.8 4.7 12l7.9-7.9a1.6 1.6 0 0 1 2.3 0l1 1a1.6 1.6 0 0 1 0 2.3L8 15.3 5.2 16Z" />
                  <path d="m11.5 5.2 3.3 3.3" />
                </svg>
              )}
            </span>

            <span className="min-w-0">
              <span className="block truncate text-[12px] font-semibold">
                {version.label}
              </span>
              <span
                className={[
                  'block text-[10px]',
                  isActive
                    ? 'text-[rgb(var(--color-surface)/0.82)]'
                    : 'text-[rgb(var(--color-text-muted))]',
                ].join(' ')}
              >
                {stateLabel}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
